import { useCallback, useEffect, useMemo, useRef, useState } from "react"

export type DebouncedCallback<TArgs extends unknown[]> = ((...args: TArgs) => void) & {
  cancel(): void
  flush(): void
  isPending(): boolean
}

/** Delay side effects, not the input's immediate draft. Pending work is canceled on disposal. */
export function useDebouncedCallback<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  delayMs = 250
): DebouncedCallback<TArgs> {
  const callbackRef = useRef(callback)
  callbackRef.current = callback
  const debounced = useMemo(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    let pending: TArgs | undefined
    const cancel = () => {
      clearTimeout(timer)
      timer = undefined
      pending = undefined
    }
    const flush = () => {
      const args = pending
      cancel()
      if (args) callbackRef.current(...args)
    }
    return Object.assign(
      (...args: TArgs) => {
        cancel()
        pending = args
        timer = setTimeout(flush, Math.max(0, delayMs))
      },
      { cancel, flush, isPending: () => pending !== undefined }
    )
  }, [delayMs])
  useEffect(() => debounced.cancel, [debounced])
  return debounced
}

export function useDebouncedValue<T>(value: T, delayMs = 250): T {
  const [settled, setSettled] = useState(value)
  const update = useCallback((next: T) => setSettled(() => next), [])
  const debounce = useDebouncedCallback(update, delayMs)
  useEffect(() => {
    debounce(value)
    return debounce.cancel
  }, [value, debounce])
  return settled
}
